import { E, S, T, iterator, parse } from "ranger-parser";
import { TAttributeParam, TParserAttribute, TParserAttributes } from "./types";
const COMMAR_EXPR = [T(",")];
const SIMPLE_EXPR = [T()];
const SERIES = [T(), T("="), S()];
/**
 *
 * This function will convert parsed parameters back to the string representation which can be used
 * with the MEA querys attributes parameter
 *
 * @param params
 * @returns
 */
export const attributesToQueryString = (params: TParserAttributes) => {
  return params.list
    .map((item) => {
      if (item.parameters.length === 0) {
        return item.name;
      }
      return `${item.name}(${item.parameters
        .map((param) => {
          if (param.tag === "int") {
            return `${param.name}=${param.value}`;
          }
          if (param.tag === "token") {
            return `${param.name}=${param.value}`;
          }
          if (param.tag === "bool") {
            return `${param.name}=${param.value}`;
          }
          return `${param.name}=${JSON.stringify(param.value)}`;
        })
        .join(",")})`;
    })
    .join(",");
};
export const findAttributesWithSeries = (
  seriesName: string,
  params: TParserAttributes
) => {
  return params.list.filter((a) => {
    if (
      a.namedParameters.series &&
      a.namedParameters.series.value === seriesName
    )
      return true;
    return false;
  });
};
export const findAttributesWithSeriesAndPeriod = (
  seriesName: string,
  periodValue: string,
  params: TParserAttributes
) => {
  return params.list.filter((a) => {
    if (
      a.namedParameters.series &&
      a.namedParameters.series.value === seriesName &&
      a.namedParameters.period &&
      a.namedParameters.period.value === periodValue
    )
      return true;
    return false;
  });
};
export const findAttributesWithPeriod = (
  periodValue: string,
  params: TParserAttributes
) => {
  return params.list.filter((a) => {
    if (
      a.namedParameters.period &&
      a.namedParameters.period.value === periodValue
    )
      return true;
    return false;
  });
};
export const findAttributes = (name: string, params: TParserAttributes) => {
  return params.list.filter(
    (a) => a.name.toLocaleLowerCase() === name.toLocaleLowerCase()
  );
};
export const parseAttributeString = (
  testAttr: string,
  separator: any = COMMAR_EXPR
): TParserAttributes => {
  const res: TParserAttributes = { list: [], queryResultNames: {} };
  const iter = iterator(parse(testAttr));
  let activeAttribute: TParserAttribute = {
    name: "",
    parameters: [],
    namedParameters: {},
  };
  iter.whileDidProceed((i) => {
    if (i.m(separator)) {
      if (activeAttribute.name) {
        res.list.push(activeAttribute);
        activeAttribute = { name: "", parameters: [], namedParameters: {} };
      }
      return;
    }
    if (
      i.m([T(), T("."), T(), T("."), T()], (list) => {
        /**
         * Something like measure.value
         */
        activeAttribute.name = list
          .map((item) => item.toTokenString())
          .join("");
      })
    ) {
      return;
    }
    if (
      i.m([T(), T("."), T()], (list) => {
        /**
         * Something like measure.value
         */
        activeAttribute.name = list
          .map((item) => item.toTokenString())
          .join("");
      })
    ) {
      return;
    }
    if (
      i.m(SIMPLE_EXPR as any, ([token]) => {
        /**
         * Simple attribute like a typename, name
         */
        activeAttribute.name = token.toTokenString();
      })
    ) {
      return;
    }
    /**
     *
     * Match expression like (...)
     *
     * Will produce the parameters for the active element
     */
    i.m([E], ([expr]) => {
      expr.whileDidProceed((expr) => {
        if (
          expr.match(SERIES as any, ([to, eq, str]) => {
            const param: TAttributeParam = {
              tag: "string",
              name: to.toTokenString(),
              value: str.toString(),
            };
            activeAttribute.parameters.push(param);
            activeAttribute.namedParameters[to.toTokenString()] = param;
          })
        ) {
          return;
        }
        // [T(), T('='), T()]
        if (
          expr.match([T(), T("="), T(), T(",")] as any, ([to, eq, str]) => {
            const param: TAttributeParam = {
              tag: "token",
              name: to.toTokenString(),
              value: str.toTokenString(),
            };
            activeAttribute.parameters.push(param);
            activeAttribute.namedParameters[to.toTokenString()] = param;
          })
        ) {
          return;
        }
        let skip = 0;
        if (
          expr.match([T(), T("=")] as any, ([to, eq, str]) => {
            // we have a match, then iterate until comma
            const getChildList = () => {
              let i = 1;
              while (true) {
                const chList = eq.peek(i);
                if (chList.length === 0) {
                  if (i > 1) {
                    return eq.peek(i - 1).filter((value, index, arr) => {
                      return index !== 0 && index !== arr.length - 1;
                    });
                  }
                  break;
                }
                const last = chList[chList.length - 1];
                if (!last) {
                  if (i > 1) {
                    return chList.filter((value, index, arr) => {
                      return index !== 0 && index !== arr.length - 1;
                    });
                  }
                  break;
                }
                if (last && last.token === ",") {
                  return chList.filter((value, index, arr) => {
                    return index !== 0 && index !== arr.length - 1;
                  });
                }
                i += 1;
              }
              return [];
            };
            let sp = -1;
            let ep = -1;
            const chList = getChildList();
            chList.forEach((v, idx) => {
              if (idx === 0) {
                sp = v.sp;
              }
              ep = v.ep;
            });
            if (sp >= 0 && ep >= 0) {
              const value = chList[0].code.code.substring(sp, ep);
              const param: TAttributeParam = {
                tag: "token",
                name: to.toTokenString(),
                value,
              };
              activeAttribute.parameters.push(param);
              activeAttribute.namedParameters[to.toTokenString()] = param;
              skip = chList.length;
            }
          })
        ) {
          expr.take(skip);
          return;
        }
        expr.m(COMMAR_EXPR);
      });
    });
  });
  if (activeAttribute.name) {
    res.list.push(activeAttribute);
  }
  res.list.forEach((item) => {
    if (item.namedParameters.as) {
      const np = item.namedParameters.as;
      if (np.tag === "string") {
        res.queryResultNames[np.value] = item;
      }
      if (np.tag === "token") {
        res.queryResultNames[np.value] = item;
      }
    } else {
      res.queryResultNames[item.name] = item;
    }
  });
  return res;
};
